/**
 * Trial Expired Banner Component
 * Shows when user's 7-day trial has ended
 */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { theme } from '../styles/theme';
import { Button } from './ui/Button';
import { AlertTriangle } from 'lucide-react';

const Banner = styled.div`
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 2px solid #ef4444;
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    text-align: center;
  }
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #ef4444;
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: #991b1b;
  margin: 0 0 0.5rem 0;
`;

const Text = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: #7f1d1d;
  margin: 0;
  line-height: 1.6;
`;

const ButtonContainer = styled.div`
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    width: 100%;
  }
`;

interface TrialExpiredBannerProps {
  onChoosePlan?: () => void;
}

export const TrialExpiredBanner: React.FC<TrialExpiredBannerProps> = ({ onChoosePlan }) => {
  const navigate = useNavigate();

  const handleChoosePlan = () => {
    if (onChoosePlan) {
      onChoosePlan();
    } else {
      navigate('/pricing');
    }
  };

  return (
    <Banner>
      <IconContainer>
        <AlertTriangle size={24} />
      </IconContainer>
      <Content>
        <Title>Your 7-day GlowLens trial has ended</Title>
        <Text>
          Choose a plan to keep using AI skin scans, diet guidance, and VIP support. 
          Start with Plus for just $4.99/month or upgrade to Pro/Elite for more features.
        </Text>
      </Content>
      <ButtonContainer>
        <Button variant="primary" onClick={handleChoosePlan}>
          Choose Plan
        </Button>
      </ButtonContainer>
    </Banner>
  );
};


